import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@satyrsmc/shared/components";
import { trpc } from "@/trpc";
import { Users, ExternalLink } from "lucide-react";
import { PaymentStatus } from "@satyrsmc/shared/lib/enums";
import type { EventAttendee } from "@satyrsmc/shared/client";
import {
  buildDailyBuckets,
  RegistrationsOverTimeChart,
} from "@/components/charts/RegistrationsOverTimeChart";

interface RideAttendeesCardProps {
  eventId: string;
  attendees: EventAttendee[];
  /** Overrides the card's title, e.g. "<Event Name> Attendees" when this
   * same card is reused for a specific event outside its own detail page
   * (the admin dashboard's upcoming-Badger-event widget). Defaults to the
   * plain "Attendees" title used on the event detail page. */
  title?: string;
}

export function RideAttendeesCard({
  eventId,
  attendees,
  title = "Attendees",
}: RideAttendeesCardProps) {
  const navigate = useNavigate();

  const contactAttendees = attendees.filter((a) => !a.is_member);
  const memberAttendees = attendees.filter((a) => a.is_member);

  const { data: adminAttendees } = trpc.admin.events.getAttendees.useQuery({ eventId });
  const { data: actionRequired } = trpc.admin.rsvps.listActionRequired.useQuery({ eventId });

  const pendingCount = actionRequired?.length ?? 0;

  const pendingPaymentCents =
    actionRequired
      ?.filter((r) => r.paymentStatus === PaymentStatus.Pending)
      .reduce((sum, r) => sum + (r.paymentAmountCents ?? 0), 0) ?? 0;

  const confirmedCount =
    adminAttendees?.filter((a) => a.payment_status === PaymentStatus.Confirmed).length ?? 0;
  const confirmedCents =
    adminAttendees
      ?.filter((a) => a.payment_status === PaymentStatus.Confirmed)
      .reduce((sum, a) => sum + (a.payment_amount_cents ?? 0), 0) ?? 0;

  const registrationDates = adminAttendees?.map((a) => a.created_at) ?? [];
  const { categories, counts } = buildDailyBuckets(registrationDates);
  const hasChartData = categories.length > 0 && counts.some((c) => c > 0);

  return (
    <Card id="ride-attendees">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="size-5" />
              {title}
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {memberAttendees.length} member{memberAttendees.length !== 1 ? "s" : ""}
              {" · "}
              {contactAttendees.length} contact{contactAttendees.length !== 1 ? "s" : ""}
              {pendingCount > 0 && (
                <>
                  {" · "}
                  <span className="text-yellow-600 font-medium">{pendingCount} pending</span>
                </>
              )}
            </p>
          </div>
          <Button
            variant="outline"
            className="relative"
            onClick={() =>
              navigate(
                pendingCount > 0
                  ? `/admin/events/${eventId}/attendees?payment=pending,refund_requested&status=`
                  : `/admin/events/${eventId}/attendees`,
              )
            }
          >
            <ExternalLink className="size-4" />
            View all
            {pendingCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 flex size-4 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground">
                {pendingCount > 9 ? "9+" : pendingCount}
              </span>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Stats row */}
        {(confirmedCount > 0 || pendingPaymentCents > 0) && (
          <div className="flex flex-wrap gap-4 text-sm">
            {confirmedCount > 0 && (
              <span className="text-muted-foreground">
                <span className="font-medium text-green-600">{confirmedCount} confirmed</span>
                {confirmedCents > 0 && <> · ${(confirmedCents / 100).toFixed(2)} paid</>}
              </span>
            )}
            {pendingPaymentCents > 0 && (
              <span className="text-yellow-600 font-medium">
                ${(pendingPaymentCents / 100).toFixed(2)} pending
              </span>
            )}
          </div>
        )}

        {/* Registration frequency chart */}
        {hasChartData ? (
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-2">
              Registrations over time
            </p>
            <RegistrationsOverTimeChart categories={categories} counts={counts} />
          </div>
        ) : attendees.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No attendees yet. Members can RSVP from the event page.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
